import { Button, cn, Form, useZodForm } from '@gymcoach/ui';
import { z } from 'zod';

import { authCopy } from './copy';
import { PasswordField } from './PasswordField';
import { useSetOwnPassword } from './usePassword';

const c = authCopy.newPassword;

function schemaFor(email: string | null, askCurrent: boolean) {
  return z
    .object({
      current_password: askCurrent ? z.string().min(1, c.currentRequired) : z.string().optional(),
      password: z
        .string()
        .min(8, c.ruleLength)
        .refine((value) => !email || value.trim().toLowerCase() !== email.toLowerCase(), c.ruleNotEmail),
      confirm: z.string().min(1, c.confirmRequired),
    })
    .superRefine((values, ctx) => {
      if (values.confirm && values.password !== values.confirm) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['confirm'], message: c.mismatch });
      }
      if (askCurrent && values.current_password && values.password === values.current_password) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: c.ruleDifferent });
      }
    });
}

type Values = z.infer<ReturnType<typeof schemaFor>>;

function Rule({ ok, children }: { ok: boolean; children: string }) {
  return (
    <li className={cn('flex items-center gap-2 text-sm transition-colors', ok ? 'text-success' : 'text-ink-muted')}>
      <span
        aria-hidden="true"
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded-full border',
          ok ? 'border-success bg-success text-ink-inverse' : 'border-line-strong',
        )}
      >
        {ok && (
          <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3.5 8.5 6.5 11.5 12.5 5" />
          </svg>
        )}
      </span>
      {children}
      <span className="sr-only">{ok ? ' (done)' : ' (not yet)'}</span>
    </li>
  );
}

/**
 * New password + "Type it again", with the rules ticking as they pass (DESIGN.md A2).
 * Used by the forced first-sign-in change, the reset link and My account.
 */
export function NewPasswordForm({
  email,
  askCurrent = false,
  submitLabel = c.submit,
  onDone,
}: {
  email: string | null;
  /** Ask for the current password first (changing it by choice). */
  askCurrent?: boolean;
  submitLabel?: string;
  onDone?: (result: { cleared: boolean }) => void;
}) {
  const save = useSetOwnPassword();
  const form = useZodForm(schemaFor(email, askCurrent), {
    defaultValues: { current_password: '', password: '', confirm: '' },
    // On submit (then live): an error appearing on blur would move the button under the pointer.
    mode: 'onSubmit',
  });
  const password = form.watch('password') ?? '';
  const current = form.watch('current_password') ?? '';

  async function onSubmit(values: Values) {
    try {
      const result = await save.mutateAsync({
        password: values.password,
        currentPassword: askCurrent ? values.current_password : undefined,
        email,
      });
      onDone?.(result);
    } catch {
      // Shown by <Form> from save.error.
    }
  }

  return (
    <Form form={form} onSubmit={onSubmit} error={save.error} ariaLabel={submitLabel}>
      {askCurrent && (
        <PasswordField form={form} name="current_password" label={c.current} autoComplete="current-password" autoFocus />
      )}
      <PasswordField form={form} name="password" label={c.password} autoComplete="new-password" autoFocus={!askCurrent} />
      <ul className="-mt-1 flex flex-col gap-1" aria-label="Password rules">
        <Rule ok={password.length >= 8}>{c.ruleLength}</Rule>
        <Rule ok={password.length > 0 && (!email || password.trim().toLowerCase() !== email.toLowerCase())}>
          {c.ruleNotEmail}
        </Rule>
        {askCurrent && <Rule ok={password.length > 0 && password !== current}>{c.ruleDifferent}</Rule>}
      </ul>
      <PasswordField form={form} name="confirm" label={c.confirm} autoComplete="new-password" />
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        loading={form.formState.isSubmitting}
        loadingLabel={c.submitting}
      >
        {submitLabel}
      </Button>
    </Form>
  );
}
